// <carousel-slider>: scroll-snap carousel that replaced jQuery slick (SLInternal/bugs#1811).
//
// Layout (slides per view, spacing, peeks, vertical strips) is pure CSS in carousel-slider.css,
// so nothing here runs on resize except re-measuring. This element only wires up:
//   [data-carousel-track]        the scroll container (required); its children are the slides
//   [data-carousel-prev|next]    arrow buttons (optional); they wrap around at either end
//   [data-carousel-dots]         empty container that gets one button per reachable position
//   [data-carousel-current]      optional element that shows the 1-based current position
//   data-autoplay="3000"         advance every N ms; pauses on hover/focus/off-screen/hidden tab,
//                                stops on the first user interaction, off for reduced motion
//   data-nav-for="<id>"          makes this carousel the thumbnail strip for another carousel
//   data-mouse-drag="false"      turns off click-and-drag with a mouse (on by default, like slick)
// It emits `carousel:change` ({ detail: { index } }) whenever the current position changes.
if (!customElements.get('carousel-slider')) {
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) this.init();
      if (!this.track) return;
      this.resizeObserver.observe(this.track);
      if (this.intersectionObserver) {
        this.paused.add('offscreen');
        this.intersectionObserver.observe(this);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      clearTimeout(this.timer);
      clearTimeout(this.targetTimer);
    }

    // One-time wiring; connect/disconnect only (re)attach observers, so moving the element
    // around the DOM never doubles up the click handlers.
    init() {
      this.initialized = true;
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.dots = this.querySelector('[data-carousel-dots]');
      this.counter = this.querySelector('[data-carousel-current]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.index = 0;
      this.pageCount = 1;
      this.positions = [0];
      this.max = 0;
      this.paused = new Set();

      this.navFor = this.dataset.navFor ? document.getElementById(this.dataset.navFor) : null;
      if (this.navFor) {
        this.setupNav();
      } else {
        this.prevButton?.addEventListener('click', () => this.userGo(this.index - 1));
        this.nextButton?.addEventListener('click', () => this.userGo(this.index + 1));
      }
      this.dots?.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-index]');
        if (dot) this.userGo(Number(dot.dataset.index));
      });

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', () => this.stop(), { passive: true });
      this.track.addEventListener('touchstart', () => this.stop(), { passive: true });
      this.track.addEventListener(
        'wheel',
        (event) => {
          if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.stop();
        },
        { passive: true }
      );

      // Fires on first layout and whenever the track changes size, including display:none -> shown
      // (tabs, accordions), so no "refresh" call is ever needed.
      this.resizeObserver = new ResizeObserver(() => this.measure());

      this.setupAutoplay();
      if (this.dataset.mouseDrag !== 'false') this.setupMouseDrag();
    }

    // Touch and trackpads scroll natively; this adds slick-style click-and-drag for mice.
    setupMouseDrag() {
      let drag = null;
      // Links and images start a native drag-and-drop on mousedown, which swallows pointer events;
      // stop that before it starts.
      this.track.addEventListener('mousedown', (event) => {
        if (event.button === 0 && this.pageCount > 1 && event.target.closest('a, img')) event.preventDefault();
      });
      this.track.addEventListener('dragstart', (event) => event.preventDefault());

      const move = (event) => {
        if (event.pointerId !== drag.id) return;
        const delta = this.isVertical ? event.clientY - drag.y : event.clientX - drag.x;
        if (!drag.moved) {
          if (Math.abs(delta) < 5) return;
          drag.moved = true;
          this.track.classList.add('is-dragging');
        }
        this.track[this.isVertical ? 'scrollTop' : 'scrollLeft'] = drag.from - delta;
      };
      const end = (event) => {
        if (event.pointerId !== drag.id) return;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        const { moved, from, index } = drag;
        drag = null;
        if (!moved) return;
        // Read where the drag ended before snapping comes back on (removing the class re-snaps).
        const distance = this.scrollPosition() - from;
        let target = this.indexFromScroll();
        this.track.classList.remove('is-dragging');
        // Swallow the click that ends a drag so links and thumbnails under the pointer don't fire.
        const swallow = (click) => {
          click.preventDefault();
          click.stopPropagation();
        };
        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
        // Settle on the nearest slide; a short flick (> 40px) still moves one slide.
        if (target === index && Math.abs(distance) > 40) target += Math.sign(distance);
        this.go(Math.max(0, Math.min(this.pageCount - 1, target)));
      };
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || this.pageCount <= 1) return;
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, from: this.scrollPosition(), index: this.index, moved: false };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', end);
      });
    }

    get slides() {
      return Array.from(this.track.children).filter((slide) => slide.offsetWidth > 0 || slide.offsetHeight > 0);
    }

    get vertical() {
      return getComputedStyle(this.track).flexDirection.startsWith('column');
    }

    scrollPosition() {
      return this.isVertical ? this.track.scrollTop : this.track.scrollLeft;
    }

    measure() {
      if (!this.track) return;
      const slides = this.slides;
      this.isVertical = this.vertical;
      const edge = this.isVertical ? 'offsetTop' : 'offsetLeft';
      const base = slides.length ? slides[0][edge] : 0;
      this.positions = slides.map((slide) => slide[edge] - base);
      this.max = this.isVertical
        ? this.track.scrollHeight - this.track.clientHeight
        : this.track.scrollWidth - this.track.clientWidth;
      // Positions past the end of the scroll range collapse into one final "end" position.
      this.pageCount = this.max > 1 ? this.positions.filter((p) => p < this.max - 1).length + 1 : 1;
      if (this.positions.length) this.positions[this.pageCount - 1] = Math.min(this.positions[this.pageCount - 1], this.max);

      const isStatic = this.pageCount <= 1;
      this.toggleAttribute('data-static', isStatic);
      if (!this.navFor) {
        if (this.prevButton) this.prevButton.hidden = isStatic;
        if (this.nextButton) this.nextButton.hidden = isStatic;
      }
      this.renderDots();
      this.target = null;
      this.setIndex(this.indexFromScroll(), true);
    }

    renderDots() {
      if (!this.dots) return;
      this.dots.hidden = this.pageCount <= 1;
      if (this.dots.childElementCount === this.pageCount) return;
      const label = this.dataset.dotLabel || 'Go to slide';
      this.dots.replaceChildren(
        ...Array.from({ length: this.pageCount }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.dataset.index = i;
          dot.setAttribute('aria-label', `${label} ${i + 1}`);
          return dot;
        })
      );
    }

    indexFromScroll() {
      const position = this.scrollPosition();
      if (position >= this.max - 1) return this.pageCount - 1;
      let closest = 0;
      for (let i = 1; i < this.pageCount; i++) {
        if (Math.abs(this.positions[i] - position) < Math.abs(this.positions[closest] - position)) closest = i;
      }
      return closest;
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        if (!this.track) return;
        // While a programmatic smooth scroll is running, hold the target index so dots don't flicker
        // through every intermediate position.
        if (this.target != null) {
          if (Math.abs(this.scrollPosition() - this.positions[this.target]) > 2) return;
          this.target = null;
        }
        this.setIndex(this.indexFromScroll());
      });
    }

    setIndex(index, force) {
      if (index === this.index && !force) return;
      const changed = index !== this.index;
      this.index = index;
      if (this.counter) this.counter.textContent = index + 1;
      if (this.dots) {
        Array.from(this.dots.children).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }
      if (changed) this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index } }));
    }

    go(index) {
      if (!this.track) return;
      if (index < 0) index = this.pageCount - 1;
      if (index >= this.pageCount) index = 0;
      this.target = index;
      clearTimeout(this.targetTimer);
      this.targetTimer = setTimeout(() => {
        this.target = null;
        this.onScroll();
      }, 1000);
      this.track.scrollTo({
        [this.isVertical ? 'top' : 'left']: this.positions[index],
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
      this.setIndex(index);
    }

    userGo(index) {
      this.stop();
      this.go(index);
    }

    setupNav() {
      const main = this.navFor;
      const mark = (index) => {
        Array.from(this.track.children).forEach((slide, i) => slide.classList.toggle('is-current', i === index));
        // Like slick's asNavFor: the current thumbnail moves to the front of the strip (as far as it can scroll).
        if (this.pageCount > 1) this.go(Math.min(index, this.pageCount - 1));
      };
      main.addEventListener('carousel:change', (event) => mark(event.detail.index));
      this.track.addEventListener('click', (event) => {
        const slide = event.target.closest('[data-carousel-track] > *');
        if (slide) main.go(Array.from(this.track.children).indexOf(slide));
      });
      this.prevButton?.addEventListener('click', () => main.go((main.index || 0) - 1));
      this.nextButton?.addEventListener('click', () => main.go((main.index || 0) + 1));
      requestAnimationFrame(() => mark(main.index || 0));
    }

    setupAutoplay() {
      this.delay = parseInt(this.dataset.autoplay, 10);
      if (!(this.delay > 0) || this.reducedMotion) return;

      this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.pause('hover'));
      this.addEventListener('pointerleave', () => this.resume('hover'));
      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', (event) => !this.contains(event.relatedTarget) && this.resume('focus'));

      this.onVisibilityChange = () => (document.hidden ? this.pause('hidden') : this.resume('hidden'));
      // Starts paused as 'offscreen' (see connectedCallback) until the first intersection callback.
      this.intersectionObserver = new IntersectionObserver(([entry]) =>
        entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')
      );
    }

    pause(reason) {
      this.paused.add(reason);
      clearTimeout(this.timer);
    }

    resume(reason) {
      this.paused.delete(reason);
      this.tick();
    }

    tick() {
      clearTimeout(this.timer);
      if (!(this.delay > 0) || this.stopped || this.paused.size || !this.isConnected) return;
      this.timer = setTimeout(() => {
        if (this.pageCount > 1) this.go(this.index + 1);
        this.tick();
      }, this.delay);
    }

    stop() {
      this.stopped = true;
      clearTimeout(this.timer);
    }
  }

  customElements.define('carousel-slider', CarouselSlider);
}
